import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Card, CardContent } from '../../../components/ui';

// Import the new components
import ApprovalHeader from './components/ApprovalHeader';
import ApprovalStatusSection from './components/ApprovalStatusSection';
import ApprovalActions from './components/ApprovalActions';
import ApprovalDetails from './components/ApprovalDetails';
import ApprovalSteps from './components/ApprovalSteps';
import ApprovalTimeline from './components/ApprovalTimeline';
import EmailNotificationHistoryModal from '../components/EmailNotificationHistoryModal';
import EmptyState from './components/EmptyState';

const CustomApprovalRequestsSplitView = ({ 
  isOpen, 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields, 
  labels, 
  onClose,
  onEdit,
  onView
}) => {
  const { theme } = useTheme();
  const [actionLoading, setActionLoading] = useState(false);
  const [showEmailHistory, setShowEmailHistory] = useState(false);

  if (!isOpen) return null;

  const handleAction = async (action, data = {}) => {
    console.log('handleAction called with:', action, 'selectedRecord:', selectedRecord);
    
    // Check if request is cancelled
    const statusValue = selectedRecord?.status?.name || selectedRecord?.status;
    const isCancelled = statusValue?.toLowerCase() === 'cancelled';
    
    if (isCancelled && action === 'edit') {
      alert('This approval request has been cancelled and cannot be edited.');
      return;
    }
    
    setActionLoading(true);
    try {
      switch (action) {
        case 'edit':
          // Use the generic onEdit handler which will open the custom form
          if (onEdit && selectedRecord) {
            console.log('Calling onEdit to open custom form');
            onEdit(selectedRecord);
          }
          setActionLoading(false);
          return; // Return early
        case 'view':
          if (onView && selectedRecord) {
            onView(selectedRecord);
          }
          break;
        case 'cancel':
          // Handle cancel action - could use config.customHandlers.onCancel if available
          if (config.customHandlers && config.customHandlers.onCancel) {
            await config.customHandlers.onCancel(selectedRecord);
          }
          break;
        default:
          console.log('Approval request action:', action, data);
          // Fallback to custom handlers if available
          if (config.customHandlers && config.customHandlers[`on${action.charAt(0).toUpperCase() + action.slice(1)}`]) {
            await config.customHandlers[`on${action.charAt(0).toUpperCase() + action.slice(1)}`](selectedRecord);
          }
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
    } finally {
      setActionLoading(false);
    }
  };


  const isOverdue = selectedRecord?.dueDate && new Date(selectedRecord.dueDate) < new Date();
  const statusValue = selectedRecord?.status?.name || selectedRecord?.status;
  const isCancelled = statusValue?.toLowerCase() === 'cancelled';
  const isPending = statusValue === 'pending' || statusValue === 'Pending';
  const canTakeAction = isPending && !isCancelled;
  const canEdit = !isCancelled && isPending; // Can only edit if not cancelled and pending

  return (
    <div 
      className={cn("hidden lg:block min-h-0 h-full pb-2")}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <Card className={cn("min-h-0 h-full flex flex-col", theme.shadow.lg)}>
        {/* Header */}
        <ApprovalHeader 
          selectedRecord={selectedRecord} 
          onClose={onClose} 
        />
        
        <CardContent className="flex-1 overflow-auto p-3 space-y-3">
          {selectedRecord ? (
            <>
              {/* Status and Key Information */}
              <ApprovalStatusSection selectedRecord={selectedRecord} />

              {/* Action Panel */}
              <div className="space-y-4">
                <ApprovalActions 
                  selectedRecord={selectedRecord}
                  canTakeAction={canTakeAction}
                  canEdit={canEdit}
                  statusValue={statusValue}
                  onHandleAction={handleAction}
                  onShowEmailHistory={() => setShowEmailHistory(true)}
                />

                {/* Details Section */}
                <ApprovalDetails selectedRecord={selectedRecord} />

                {/* Steps Section */}
                <ApprovalSteps selectedRecord={selectedRecord} />

                {/* Timeline Section */}
                <ApprovalTimeline 
                  selectedRecord={selectedRecord} 
                  isOverdue={isOverdue} 
                />
              </div>
            </>
          ) : (
            <EmptyState />
          )}
        </CardContent>
      </Card>

      {/* Email Notification History Modal */}
      {selectedRecord && showEmailHistory && (
        <EmailNotificationHistoryModal
          isOpen={showEmailHistory}
          onClose={() => setShowEmailHistory(false)}
          approvalRequestId={selectedRecord.id}
        />
      )}
    </div>
  );
};

export default CustomApprovalRequestsSplitView;
